'use client';
import React, { useState, useEffect, useRef } from 'react';
import AppLayout from '@/components/AppLayout';
import Link from 'next/link';
import { AlertTriangle, Lightbulb, Globe, TrendingUp, Rocket, ChevronRight, ChevronLeft, CheckCircle2, ArrowRight, DollarSign, BarChart3, MapPin, Shield, Users, Zap, Building2, Target, Award, ExternalLink, Play, Pause, Clock, Star, Activity } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


/* ─── Types ─────────────────────────────────────────── */
interface Slide {
  id: string;
  step: number;
  label: string;
  icon: React.ElementType;
  color: string;
  bg: string;
  border: string;
}

/* ─── Slide Manifest ─────────────────────────────────── */
const slides: Slide[] = [
  { id: 'problem',     step: 1, label: 'The Problem',         icon: AlertTriangle, color: '#f87171', bg: 'rgba(248,113,113,0.08)', border: 'rgba(248,113,113,0.25)' },
  { id: 'solution',    step: 2, label: 'LRA Nexus Solution',  icon: Lightbulb,     color: '#fbbf24', bg: 'rgba(251,191,36,0.08)',  border: 'rgba(251,191,36,0.25)'  },
  { id: 'africa',      step: 3, label: 'Africa Proof Points', icon: Globe,         color: '#34d399', bg: 'rgba(52,211,153,0.08)',  border: 'rgba(52,211,153,0.25)'  },
  { id: 'roi',         step: 4, label: 'ROI Summary',         icon: TrendingUp,    color: '#60a5fa', bg: 'rgba(96,165,250,0.08)',  border: 'rgba(96,165,250,0.25)'  },
  { id: 'cta',         step: 5, label: 'Call to Action',      icon: Rocket,        color: '#a78bfa', bg: 'rgba(167,139,250,0.08)', border: 'rgba(167,139,250,0.25)' },
];

/* ─── Stat Card ──────────────────────────────────────── */
function StatCard({ value, label, sub, color }: { value: string; label: string; sub?: string; color: string }) {
  return (
    <div className="rounded-xl p-4 flex flex-col gap-1" style={{ background: `rgba(${color},0.08)`, border: `1px solid rgba(${color},0.2)` }}>
      <span className="text-2xl font-black" style={{ color: `rgb(${color})` }}>{value}</span>
      <span className="text-sm font-semibold" style={{ color: 'var(--dark-text)' }}>{label}</span>
      {sub && <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>{sub}</span>}
    </div>
  );
}

/* ─── Feature Pill ───────────────────────────────────── */
function FeaturePill({ icon: Icon, label, color }: { icon: React.ElementType; label: string; color: string }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium"
      style={{ background: `${color}18`, border: `1px solid ${color}30`, color }}>
      <Icon size={14} />
      {label}
    </div>
  );
}

/* ─── Country Card ───────────────────────────────────── */
function CountryCard({ flag, country, investment, result, source }: {
  flag: string; country: string; investment: string; result: string; source: string;
}) {
  return (
    <div className="rounded-xl p-4 flex flex-col gap-2" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--dark-border)' }}>
      <div className="flex items-center gap-2">
        <span className="text-2xl">{flag}</span>
        <span className="font-bold text-sm" style={{ color: 'var(--dark-text)' }}>{country}</span>
      </div>
      <div className="text-lg font-black" style={{ color: '#34d399' }}>{investment}</div>
      <div className="text-xs" style={{ color: 'var(--dark-muted)' }}>{result}</div>
      <div className="text-xs font-semibold mt-1" style={{ color: '#60a5fa' }}>{source}</div>
    </div>
  );
}

/* ─── ROI Row ────────────────────────────────────────── */
function ROIRow({ label, before, after, color }: { label: string; before: string; after: string; color: string }) {
  return (
    <div className="flex items-center gap-3 py-3 border-b" style={{ borderColor: 'var(--dark-border)' }}>
      <div className="flex-1 text-sm font-medium" style={{ color: 'var(--dark-text)' }}>{label}</div>
      <div className="text-sm line-through" style={{ color: 'var(--dark-muted)' }}>{before}</div>
      <ArrowRight size={14} style={{ color }} />
      <div className="text-sm font-bold" style={{ color }}>{after}</div>
    </div>
  );
}

/* ─── Slide Content Components ───────────────────────── */
function ProblemSlide() {
  return (
    <div className="flex flex-col gap-6 h-full">
      {/* Hero statement */}
      <div className="rounded-2xl p-6" style={{ background: 'linear-gradient(135deg, rgba(248,113,113,0.12) 0%, rgba(251,191,36,0.08) 100%)', border: '1px solid rgba(248,113,113,0.3)' }}>
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(248,113,113,0.2)' }}>
            <AlertTriangle size={28} style={{ color: '#f87171' }} />
          </div>
          <div>
            <h2 className="text-2xl font-black mb-2" style={{ color: 'var(--dark-text)' }}>
              Liberia Collects <span style={{ color: '#f87171' }}>0.1% of GDP</span> in Property Tax
            </h2>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--dark-muted)' }}>
              The IMF benchmark for comparable economies is <strong style={{ color: '#fbbf24' }}>0.5–1.0% of GDP</strong>. 
              Liberia's property tax system is manual, fragmented, and severely under-resourced — 
              leaving an estimated <strong style={{ color: '#f87171' }}>$19.26M/year</strong> uncollected.
            </p>
          </div>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard value="$19.26M" label="Annual Revenue Gap" sub="IMF estimate" color="248,113,113" />
        <StatCard value="0.1%" label="Property Tax / GDP" sub="vs 0.5–1.0% IMF target" color="251,191,36" />
        <StatCard value="~60%" label="Properties Unregistered" sub="World Bank, 2023" color="248,113,113" />
        <StatCard value="15 Counties" label="Manual Paper Systems" sub="No digital integration" color="251,191,36" />
      </div>

      {/* Root causes */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {[
          { icon: MapPin, title: 'No Property Registry', desc: 'Thousands of properties exist off-map — no GIS, no parcel IDs, no ownership chain.', color: '#f87171' },
          { icon: Users, title: 'Taxpayer Identity Gap', desc: 'Manual ledgers, duplicate records, and no biometric verification create ghost taxpayers.', color: '#fbbf24' },
          { icon: DollarSign, title: 'Revenue Leakage', desc: 'Informal payments, no receipts, and zero enforcement mean collections stay at 30% of potential.', color: '#f87171' },
        ].map(({ icon: Icon, title, desc, color }) => (
          <div key={title} className="rounded-xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--dark-border)' }}>
            <div className="flex items-center gap-2 mb-2">
              <Icon size={16} style={{ color }} />
              <span className="text-sm font-bold" style={{ color: 'var(--dark-text)' }}>{title}</span>
            </div>
            <p className="text-xs leading-relaxed" style={{ color: 'var(--dark-muted)' }}>{desc}</p>
          </div>
        ))}
      </div>

      {/* Cost of inaction ticker */}
      <div className="rounded-xl px-5 py-3 flex items-center gap-3" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)' }}>
        <div className="w-2 h-2 rounded-full animate-pulse flex-shrink-0" style={{ background: '#ef4444' }} />
        <span className="text-xs font-semibold" style={{ color: '#f87171' }}>
          Every day without action = <strong>$52,767 lost</strong> in uncollected property tax revenue
        </span>
        <span className="text-xs ml-auto" style={{ color: 'var(--dark-muted)' }}>Source: IMF Fiscal Monitor 2023</span>
      </div>
    </div>
  );
}

function SolutionSlide() {
  const features = [
    { icon: MapPin, label: 'GIS Parcel Mapping', color: '#fbbf24' },
    { icon: Users, label: 'Taxpayer Registry', color: '#34d399' },
    { icon: Shield, label: 'Compliance Engine', color: '#60a5fa' },
    { icon: BarChart3, label: 'Revenue Analytics', color: '#fbbf24' },
    { icon: Zap, label: 'AI Discovery', color: '#a78bfa' },
    { icon: Building2, label: 'County Offices', color: '#34d399' },
    { icon: Activity, label: 'Drone Integration', color: '#60a5fa' },
    { icon: DollarSign, label: 'Collections Module', color: '#fbbf24' },
  ];

  return (
    <div className="flex flex-col gap-6 h-full">
      {/* Hero */}
      <div className="rounded-2xl p-6" style={{ background: 'linear-gradient(135deg, rgba(251,191,36,0.12) 0%, rgba(52,211,153,0.08) 100%)', border: '1px solid rgba(251,191,36,0.3)' }}>
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(251,191,36,0.2)' }}>
            <Lightbulb size={28} style={{ color: '#fbbf24' }} />
          </div>
          <div>
            <h2 className="text-2xl font-black mb-2" style={{ color: 'var(--dark-text)' }}>
              LRA Nexus: <span style={{ color: '#fbbf24' }}>End-to-End</span> Property Tax Intelligence
            </h2>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--dark-muted)' }}>
              A unified, AI-powered platform that digitises every stage of Liberia's property tax lifecycle — 
              from satellite discovery to enforcement — aligned with <strong style={{ color: '#34d399' }}>World Bank GIS Standards</strong> and 
              <strong style={{ color: '#60a5fa' }}> IMF fiscal benchmarks</strong>.
            </p>
          </div>
        </div>
      </div>

      {/* 9-stage pipeline */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: 'var(--dark-muted)' }}>9-Stage Property Tax Cycle — Fully Automated</p>
        <div className="flex flex-wrap gap-2">
          {[
            'Property Discovery (Identification), Mapping and Registration',
            'Property Assessment (Valuation)',
            'Property Rates & Exemptions',
            'Property Billing',
            'Property Taxpayer Education & Engagement',
            'Property Appeals',
            'Property Payments',
            'Property Voluntary Compliance',
            'Property Enforcement'
          ].map((stage, i) => (
            <div key={stage} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold"
              style={{ background: 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.25)', color: '#fbbf24' }}>
              <span className="w-4 h-4 rounded-full flex items-center justify-center text-xs font-black"
                style={{ background: 'rgba(251,191,36,0.3)' }}>{i + 1}</span>
              {stage}
            </div>
          ))}
        </div>
      </div>

      {/* Feature grid */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: 'var(--dark-muted)' }}>Platform Modules</p>
        <div className="flex flex-wrap gap-2">
          {features.map(f => <FeaturePill key={f.label} icon={f.icon} label={f.label} color={f.color} />)}
        </div>
      </div>

      {/* WB alignment */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {[
          { label: 'World Bank GIS', value: '86%', sub: 'Compliance Score', color: '#34d399' },
          { label: 'IMF Fiscal Target', value: '0.5%', sub: 'GDP achievable in 3 yrs', color: '#60a5fa' },
          { label: 'Deployment Time', value: '6 mo', sub: 'Pilot county go-live', color: '#fbbf24' },
        ].map(({ label, value, sub, color }) => (
          <div key={label} className="rounded-xl p-4 text-center" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--dark-border)' }}>
            <div className="text-3xl font-black mb-1" style={{ color }}>{value}</div>
            <div className="text-sm font-bold" style={{ color: 'var(--dark-text)' }}>{label}</div>
            <div className="text-xs mt-0.5" style={{ color: 'var(--dark-muted)' }}>{sub}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AfricaSlide() {
  const countries = [
    { flag: '🇷🇼', country: 'Rwanda', investment: '$47M', result: '+340% property tax revenue in 5 years. 2.1M parcels digitised.', source: 'World Bank, 2022' },
    { flag: '🇬🇭', country: 'Ghana', investment: '$62M', result: 'GIS-linked valuation roll. 68% compliance rate achieved.', source: 'IMF Fiscal Monitor, 2021' },
    { flag: '🇰🇪', country: 'Kenya', investment: '$85M', result: 'Nairobi property tax tripled. 1.4M properties on digital roll.', source: 'AfDB, 2023' },
    { flag: '🇿🇦', country: 'South Africa', investment: '$210M', result: 'National valuation system. 99% urban coverage.', source: 'World Bank, 2020' },
    { flag: '🇺🇬', country: 'Uganda', investment: '$38M', result: 'Kampala revenue up 280%. Drone mapping deployed.', source: 'UN-Habitat, 2022' },
    { flag: '🇹🇿', country: 'Tanzania', investment: '$55M', result: '3.8M parcels registered. Mobile payment integration.', source: 'IMF, 2023' },
  ];

  return (
    <div className="flex flex-col gap-6 h-full">
      {/* Hero */}
      <div className="rounded-2xl p-6" style={{ background: 'linear-gradient(135deg, rgba(52,211,153,0.12) 0%, rgba(96,165,250,0.08) 100%)', border: '1px solid rgba(52,211,153,0.3)' }}>
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(52,211,153,0.2)' }}>
            <Globe size={28} style={{ color: '#34d399' }} />
          </div>
          <div>
            <h2 className="text-2xl font-black mb-2" style={{ color: 'var(--dark-text)' }}>
              Africa Has Already <span style={{ color: '#34d399' }}>Invested $497M+</span> in This Approach
            </h2>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--dark-muted)' }}>
              Six African nations have built digital property tax systems using the same methodology as LRA Nexus — 
              at a combined cost exceeding <strong style={{ color: '#34d399' }}>$497 million USD</strong>. 
              LRA Nexus delivers the same capability for Liberia at a fraction of the cost.
            </p>
          </div>
        </div>
      </div>

      {/* Country cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        {countries.map(c => <CountryCard key={c.country} {...c} />)}
      </div>

      {/* Total callout */}
      <div className="rounded-xl p-4 flex items-center gap-4" style={{ background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.25)' }}>
        <Award size={24} style={{ color: '#34d399' }} />
        <div className="flex-1">
          <p className="text-sm font-bold" style={{ color: 'var(--dark-text)' }}>
            $497M+ invested across Africa proves this model works. LRA Nexus brings the same results to Liberia — purpose-built, locally deployed.
          </p>
        </div>
        <Link href="/nine-circle-methodology" className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg whitespace-nowrap"
          style={{ background: 'rgba(52,211,153,0.15)', color: '#34d399', border: '1px solid rgba(52,211,153,0.3)' }}>
          Deep Dive <ExternalLink size={12} />
        </Link>
      </div>
    </div>
  );
}

function ROISlide() {
  return (
    <div className="flex flex-col gap-6 h-full">
      {/* Hero */}
      <div className="rounded-2xl p-6" style={{ background: 'linear-gradient(135deg, rgba(96,165,250,0.12) 0%, rgba(167,139,250,0.08) 100%)', border: '1px solid rgba(96,165,250,0.3)' }}>
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(96,165,250,0.2)' }}>
            <TrendingUp size={28} style={{ color: '#60a5fa' }} />
          </div>
          <div>
            <h2 className="text-2xl font-black mb-2" style={{ color: 'var(--dark-text)' }}>
              Projected ROI: <span style={{ color: '#60a5fa' }}>$19.26M/yr</span> Additional Revenue
            </h2>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--dark-muted)' }}>
              Closing Liberia's property tax gap to the IMF benchmark generates transformational fiscal capacity — 
              funding schools, roads, and public services without new borrowing.
            </p>
          </div>
        </div>
      </div>

      {/* Before / After */}
      <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--dark-border)' }}>
        <div className="px-4 py-2 text-xs font-semibold uppercase tracking-widest" style={{ background: 'rgba(255,255,255,0.03)', color: 'var(--dark-muted)' }}>
          Before LRA Nexus → After LRA Nexus
        </div>
        <div className="px-4">
          <ROIRow label="Annual Property Tax Revenue" before="~$1.5M" after="$20.76M" color="#34d399" />
          <ROIRow label="Registered Properties" before="~40%" after="95%+" color="#60a5fa" />
          <ROIRow label="Collection Rate" before="30%" after="82%" color="#fbbf24" />
          <ROIRow label="Assessment Accuracy" before="Manual / Estimated" after="GIS-Verified" color="#34d399" />
          <ROIRow label="Compliance Rate" before="~25%" after="68%+" color="#60a5fa" />
          <ROIRow label="Time to Bill" before="6–12 months" after="Real-time" color="#fbbf24" />
        </div>
      </div>

      {/* Payback timeline */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { period: 'Year 1', value: '$4.2M', sub: 'Pilot 3 counties · 12,000 properties', color: '#60a5fa' },
          { period: 'Year 2', value: '$11.8M', sub: 'Scale to 8 counties · 45,000 properties', color: '#a78bfa' },
          { period: 'Year 3', value: '$20.76M', sub: 'Full national rollout · 15 counties', color: '#34d399' },
        ].map(({ period, value, sub, color }) => (
          <div key={period} className="rounded-xl p-4 text-center" style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${color}30` }}>
            <div className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: 'var(--dark-muted)' }}>{period}</div>
            <div className="text-2xl font-black" style={{ color }}>{value}</div>
            <div className="text-xs mt-1" style={{ color: 'var(--dark-muted)' }}>{sub}</div>
          </div>
        ))}
      </div>

      {/* IMF citation */}
      <div className="rounded-xl px-4 py-3 flex items-center gap-3" style={{ background: 'rgba(96,165,250,0.06)', border: '1px solid rgba(96,165,250,0.2)' }}>
        <Star size={14} style={{ color: '#60a5fa' }} />
        <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>
          <strong style={{ color: '#60a5fa' }}>IMF Fiscal Monitor 2023:</strong> Countries that digitise property tax administration see average revenue increases of <strong style={{ color: 'var(--dark-text)' }}>280–340%</strong> within 5 years of deployment.
        </p>
      </div>
    </div>
  );
}

function CTASlide() {
  const actions = [
    { icon: Target, title: 'Approve Pilot Programme', desc: 'Authorise a 3-county pilot (Montserrado, Margibi, Bong) — 6-month deployment, live results.', color: '#a78bfa', href: '/problem-solution' },
    { icon: BarChart3, title: 'Review Full Evidence Base', desc: 'Explore IMF benchmarks, Africa proof points, and the 9-circle methodology in detail.', color: '#60a5fa', href: '/nine-circle-methodology' },
    { icon: MapPin, title: 'See the GIS Platform Live', desc: 'Walk through the GIS Property Hub, drone mapping, and parcel registry in the live demo.', color: '#34d399', href: '/gis-property-hub' },
  ];

  return (
    <div className="flex flex-col gap-6 h-full">
      {/* Hero */}
      <div className="rounded-2xl p-6" style={{ background: 'linear-gradient(135deg, rgba(167,139,250,0.12) 0%, rgba(251,191,36,0.08) 100%)', border: '1px solid rgba(167,139,250,0.3)' }}>
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(167,139,250,0.2)' }}>
            <Rocket size={28} style={{ color: '#a78bfa' }} />
          </div>
          <div>
            <h2 className="text-2xl font-black mb-2" style={{ color: 'var(--dark-text)' }}>
              The Decision: <span style={{ color: '#a78bfa' }}>Act Now</span> or Lose Another $19M
            </h2>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--dark-muted)' }}>
              Every year without LRA Nexus costs Liberia <strong style={{ color: '#f87171' }}>$19.26M in uncollected revenue</strong>. 
              The technology is ready. The evidence is clear. Six African nations have already proven the model. 
              <strong style={{ color: '#a78bfa' }}> Liberia is next.</strong>
            </p>
          </div>
        </div>
      </div>

      {/* Action cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {actions.map(({ icon: Icon, title, desc, color, href }) => (
          <Link key={title} href={href}
            className="rounded-xl p-5 flex flex-col gap-3 transition-all duration-200 hover:scale-[1.02] cursor-pointer"
            style={{ background: `${color}10`, border: `1px solid ${color}30` }}>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${color}20` }}>
              <Icon size={20} style={{ color }} />
            </div>
            <div>
              <p className="text-sm font-bold mb-1" style={{ color: 'var(--dark-text)' }}>{title}</p>
              <p className="text-xs leading-relaxed" style={{ color: 'var(--dark-muted)' }}>{desc}</p>
            </div>
            <div className="flex items-center gap-1 text-xs font-semibold mt-auto" style={{ color }}>
              Explore <ArrowRight size={12} />
            </div>
          </Link>
        ))}
      </div>

      {/* Commitment checklist */}
      <div className="rounded-xl p-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--dark-border)' }}>
        <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: 'var(--dark-muted)' }}>LRA Nexus Commitment to Liberia</p>
        <div className="grid grid-cols-2 gap-2">
          {[
            'World Bank GIS-aligned architecture',
            'IMF fiscal benchmark targeting',
            'Local data sovereignty — hosted in Liberia',
            '6-month pilot to live results',
            'Full 15-county scalability built-in',
            'Ongoing training & capacity building',
          ].map(item => (
            <div key={item} className="flex items-center gap-2 text-xs" style={{ color: 'var(--dark-text)' }}>
              <CheckCircle2 size={14} style={{ color: '#34d399', flexShrink: 0 }} />
              {item}
            </div>
          ))}
        </div>
      </div>

      {/* Final urgency */}
      <div className="rounded-xl px-5 py-3 flex items-center gap-3" style={{ background: 'rgba(167,139,250,0.08)', border: '1px solid rgba(167,139,250,0.25)' }}>
        <Clock size={16} style={{ color: '#a78bfa' }} />
        <p className="text-xs font-semibold" style={{ color: '#a78bfa' }}>
          Pilot deployment can begin within <strong>30 days</strong> of approval. Revenue impact measurable within <strong>90 days</strong>.
        </p>
      </div>
    </div>
  );
}

/* ─── Slide Content Map ──────────────────────────────── */
const slideContent: Record<string, React.ReactNode> = {
  problem:  <ProblemSlide />,
  solution: <SolutionSlide />,
  africa:   <AfricaSlide />,
  roi:      <ROISlide />,
  cta:      <CTASlide />,
};

/* ─── Main Component ─────────────────────────────────── */
export default function ExecBriefingClient() {
  const [current, setCurrent] = useState(0);
  const [autoPlay, setAutoPlay] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const slide = slides[current];

  // Auto-play logic
  useEffect(() => {
    if (autoPlay) {
      timerRef.current = setTimeout(() => {
        setCurrent(p => (p < slides.length - 1 ? p + 1 : p));
      }, 8000);
    }
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [autoPlay, current]);

  const goTo = (idx: number) => {
    setCurrent(idx);
    if (timerRef.current) clearTimeout(timerRef.current);
  };

  const prev = () => goTo(Math.max(0, current - 1));
  const next = () => goTo(Math.min(slides.length - 1, current + 1));

  return (
    <AppLayout>
      <div className="min-h-screen px-4 py-5 lg:px-6 xl:px-8 max-w-screen-2xl mx-auto flex flex-col gap-5">

        {/* ── Page Header ── */}
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Link href="/" className="text-xs flex items-center gap-1 hover:opacity-80 transition-opacity"
                style={{ color: 'var(--dark-muted)' }}>Dashboard</Link>
              <ChevronRight size={12} style={{ color: 'var(--dark-muted)' }} />
              <span className="text-xs font-semibold" style={{ color: 'var(--accent)' }}>Executive Briefing</span>
            </div>
            <h1 className="text-2xl font-black" style={{ color: 'var(--dark-text)' }}>
              LRA Nexus — Executive Briefing
            </h1>
            <p className="text-sm mt-0.5" style={{ color: 'var(--dark-muted)' }}>
              Decision-maker walkthrough · Problem → Solution → Evidence → ROI → Action
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setAutoPlay(p => !p)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-all duration-150"
              style={{
                background: autoPlay ? 'rgba(167,139,250,0.15)' : 'rgba(255,255,255,0.05)',
                border: autoPlay ? '1px solid rgba(167,139,250,0.4)' : '1px solid var(--dark-border)',
                color: autoPlay ? '#a78bfa' : 'var(--dark-muted)',
              }}
            >
              {autoPlay ? <Pause size={13} /> : <Play size={13} />}
              {autoPlay ? 'Auto-playing' : 'Auto-play'}
            </button>
          </div>
        </div>

        {/* ── Step Indicator ── */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1">
          {slides.map((s, i) => {
            const Icon = s.icon;
            const isActive = i === current;
            const isDone = i < current;
            return (
              <button
                key={s.id}
                onClick={() => goTo(i)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap transition-all duration-200 flex-shrink-0"
                style={{
                  background: isActive ? s.bg : isDone ? 'rgba(52,211,153,0.06)' : 'rgba(255,255,255,0.03)',
                  border: isActive ? `1px solid ${s.border}` : isDone ? '1px solid rgba(52,211,153,0.2)' : '1px solid var(--dark-border)',
                  color: isActive ? s.color : isDone ? '#34d399' : 'var(--dark-muted)',
                  transform: isActive ? 'scale(1.03)' : 'scale(1)',
                }}
              >
                {isDone ? (
                  <CheckCircle2 size={15} style={{ color: '#34d399' }} />
                ) : (
                  <Icon size={15} />
                )}
                <span className="hidden sm:inline">{s.label}</span>
                <span className="sm:hidden">{s.step}</span>
              </button>
            );
          })}
          {/* Progress bar */}
          <div className="flex-1 hidden lg:block ml-2">
            <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${((current + 1) / slides.length) * 100}%`,
                  background: `linear-gradient(90deg, ${slides[0].color}, ${slide.color})`,
                }}
              />
            </div>
          </div>
        </div>

        {/* ── Slide Area ── */}
        <div
          className="flex-1 rounded-2xl p-6 lg:p-8 transition-all duration-300"
          style={{ background: 'var(--dark-surface)', border: `1px solid ${slide.border}`, minHeight: '520px' }}
        >
          {/* Slide header */}
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: slide.bg, border: `1px solid ${slide.border}` }}>
              <slide.icon size={20} style={{ color: slide.color }} />
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'var(--dark-muted)' }}>
                Step {slide.step} of {slides.length}
              </div>
              <h2 className="text-lg font-black" style={{ color: slide.color }}>{slide.label}</h2>
            </div>
          </div>

          {/* Content */}
          {slideContent[slide.id]}
        </div>

        {/* ── Navigation Controls ── */}
        <div className="flex items-center justify-between">
          <button
            onClick={prev}
            disabled={current === 0}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-150 disabled:opacity-30 disabled:cursor-not-allowed"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid var(--dark-border)', color: 'var(--dark-text)' }}
          >
            <ChevronLeft size={16} /> Previous
          </button>

          <div className="flex items-center gap-2">
            {slides.map((s, i) => (
              <button
                key={s.id}
                onClick={() => goTo(i)}
                className="w-2.5 h-2.5 rounded-full transition-all duration-200"
                style={{
                  background: i === current ? slide.color : 'rgba(255,255,255,0.15)',
                  transform: i === current ? 'scale(1.4)' : 'scale(1)',
                }}
              />
            ))}
          </div>

          {current < slides.length - 1 ? (
            <button
              onClick={next}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-150"
              style={{ background: slide.bg, border: `1px solid ${slide.border}`, color: slide.color }}
            >
              Next <ChevronRight size={16} />
            </button>
          ) : (
            <Link
              href="/problem-solution"
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-150"
              style={{ background: 'rgba(167,139,250,0.15)', border: '1px solid rgba(167,139,250,0.4)', color: '#a78bfa' }}
            >
              Full Analysis <ExternalLink size={14} />
            </Link>
          )}
        </div>

      </div>
    </AppLayout>
  );
}
